import React, { useEffect } from 'react';
import { Form } from 'rhombus-react';
import { CREATIVE_ASSET_TYPES } from 'utils/constants';
import { useSelector } from 'react-redux';
import { getFormValues } from "redux-form";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "../../featureFlag/featureFlag";
import { isBuildMethodSupported } from "social/utils";

import './BuildMethodRadio.css';

const MULTIPLE_VERSIONS_TOOLTIP_MESSAGE = "Asset source cannot be edited once the creative has versions";
const AE_EXISTING_CREATIVE_TOOLTIP = "Build method cannot be changed for existing After Effects creatives";
const NON_AE_EXISTING_CREATIVE_TOOLTIP = "After Effects cannot be selected for existing creatives";

export default function BuildMethodRadio({ input, meta, customTip, isAESupported: isAESupportedProp, isAdStudioSupported: isAdStudioSupportedProp, ...props }){
	const inputProps = {
		...input,
		...props,
		meta,
	};

	const { disabled } = inputProps;
	const doVersionsExist = useSelector(state => state.creativeDetails?.numberOfVersions > 1);
	const creativeId = useSelector(state => state.creativeDetails?.id);
	const isExistingCreative = !!creativeId;
	const isCurrentlyAE = input.value === CREATIVE_ASSET_TYPES.AFTER_EFFECTS;
	const isNonAEDisabledByEdit = isExistingCreative && isCurrentlyAE;
	const isAEDisabledByEdit = isExistingCreative && !isCurrentlyAE;

	let tip = customTip;
	if (!tip) {
		tip = doVersionsExist ? MULTIPLE_VERSIONS_TOOLTIP_MESSAGE: "";
	}

	const nonAETip = (!customTip && !doVersionsExist && isNonAEDisabledByEdit) ? AE_EXISTING_CREATIVE_TOOLTIP : tip;
	const aeTip = (!customTip && !doVersionsExist && isAEDisabledByEdit) ? NON_AE_EXISTING_CREATIVE_TOOLTIP : tip;

	const isRadioDisabled = disabled || doVersionsExist;
	const { name } = input || {};
	const [adType] = name?.split(".") || [];
	const creativeValues = useSelector(state => getFormValues(meta?.form)(state)?.[adType] || {});
	const isCreativeDynamic = creativeValues?.isDynamic;
	const isAESocialVersionsFlagEnabled = useFlag(FEATURE_FLAGS.AFTER_EFFECTS_SOCIAL_VERSIONS);

	const context = { adType, isDynamic: isCreativeDynamic };
	const isAESupported = isAESupportedProp !== undefined ? isAESupportedProp : isBuildMethodSupported(CREATIVE_ASSET_TYPES.AFTER_EFFECTS, context);
	const isImpSupported = isBuildMethodSupported(CREATIVE_ASSET_TYPES.IMPOSIUM, context);
	const isAdStudioSupported = isAdStudioSupportedProp !== undefined ? isAdStudioSupportedProp : isBuildMethodSupported(CREATIVE_ASSET_TYPES.ADSTUDIO, context);

	useEffect(() => {
		const isCurrentMethodSupported =
			(input.value !== CREATIVE_ASSET_TYPES.AFTER_EFFECTS || isAESupported)
			&& (input.value !== CREATIVE_ASSET_TYPES.IMPOSIUM || isImpSupported)
			&& (input.value !== CREATIVE_ASSET_TYPES.ADSTUDIO || isAdStudioSupported);
		if (!isCurrentMethodSupported && typeof input.onChange === 'function'){
			input.onChange(CREATIVE_ASSET_TYPES.PREBUILT);
		}
	}, [isCreativeDynamic, isAESupported, isImpSupported, isAdStudioSupported, input]);

	const classes = isAESocialVersionsFlagEnabled ? "full-width e-form__control-wrapper build-method-radio" : "build-method-radio";

	return (
		<div className={classes}>
			<span className="build-method-header">Build Method:</span>
			<div className="pre-built-radio" data-tip={nonAETip}>
				<Form.Checkbox
					{...inputProps}
					label="Pre-built Assets"
					type="radio"
					disabled={isRadioDisabled || isNonAEDisabledByEdit}
					value={CREATIVE_ASSET_TYPES.PREBUILT}
					checked={input.value === CREATIVE_ASSET_TYPES.PREBUILT}
				/>
			</div>
			<div className="ad-studio-radio" data-tip={nonAETip}>
				<Form.Checkbox
					{...inputProps}
					label="Ad Studio"
					type="radio"
					disabled={isRadioDisabled || !isAdStudioSupported || isNonAEDisabledByEdit}
					value={CREATIVE_ASSET_TYPES.ADSTUDIO}
					checked={input.value === CREATIVE_ASSET_TYPES.ADSTUDIO && isAdStudioSupported}
				/>
			</div>
			<div className="imposium-radio" data-tip={nonAETip}>
				<Form.Checkbox
					{...inputProps}
					label="Imposium"
					type="radio"
					disabled={isRadioDisabled || !isImpSupported || isNonAEDisabledByEdit}
					value={CREATIVE_ASSET_TYPES.IMPOSIUM}
					checked={input.value === CREATIVE_ASSET_TYPES.IMPOSIUM && isImpSupported}
				/>
			</div>
			{isAESocialVersionsFlagEnabled && (
				<div className="after-effects-radio" data-tip={aeTip}>
					<Form.Checkbox
						{...inputProps}
						label="After Effects"
						type="radio"
						disabled={isRadioDisabled || !isAESupported || isAEDisabledByEdit}
						value={CREATIVE_ASSET_TYPES.AFTER_EFFECTS}
						checked={input.value === CREATIVE_ASSET_TYPES.AFTER_EFFECTS && isAESupported}
					/>
				</div>
			)}
		</div>
	);
}

BuildMethodRadio.displayName = "BuildMethodRadio";

export const assetType = {
	name: "assetType",
	component: BuildMethodRadio,
};
